import { useEffect, useState } from 'react';
import {
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import Typography from '@material-ui/core/Typography';
import {
  ClusterStatus,
  EnvironmentAllocation,
  KUBERNETES_ID_ANNOTATION,
  ServiceAllocationResponse,
  addAllocations,
  formatCost,
  isServiceAllocationResponse,
  staleClusters,
} from './lib';

const columns: TableColumn<Record<string, string>>[] = [
  { title: 'Environment', field: 'environment' },
  { title: 'CPU', field: 'cpu' },
  { title: 'Memory', field: 'memory' },
  { title: 'Storage', field: 'storage' },
  { title: 'Total', field: 'total' },
];

const toRow = (cost: EnvironmentAllocation): Record<string, string> => ({
  environment: cost.environment,
  cpu: formatCost(cost.allocation.cpuCost),
  memory: formatCost(cost.allocation.ramCost),
  storage: formatCost(cost.allocation.pvCost),
  total: formatCost(cost.allocation.totalCost),
});

function ClusterCaption({ clusters }: { clusters: ClusterStatus[] }) {
  const stale = staleClusters(clusters);
  if (stale.length === 0) {
    return null;
  }
  return (
    <>
      {stale.map(cluster => (
        <Typography key={cluster.name} variant="body2" color="textSecondary">
          {cluster.asOf
            ? `Some figures may be stale — ${cluster.name} last updated ${cluster.asOf}.`
            : `No data yet from ${cluster.name}.`}
        </Typography>
      ))}
    </>
  );
}

type SectionState =
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'loaded'; response: ServiceAllocationResponse };

/** Loading, error and empty states are scoped to this section only — none
 * of them gate the Azure section. */
export const KubernetesCostSection = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const kubernetesId =
    entity.metadata.annotations?.[KUBERNETES_ID_ANNOTATION] ?? '';

  const [state, setState] = useState<SectionState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('cost-allocation');
      const response = await fetchApi.fetch(
        `${baseUrl}/service/${encodeURIComponent(kubernetesId)}`,
      );
      if (!response.ok) {
        throw new Error(`Could not load Kubernetes cost (${response.status}).`);
      }
      const body = await response.json();
      if (!isServiceAllocationResponse(body)) {
        throw new Error('Kubernetes cost response was not in the expected shape.');
      }
      if (!cancelled) {
        setState({ kind: 'loaded', response: body });
      }
    };
    load().catch(e => {
      if (!cancelled) {
        setState({ kind: 'error', error: e as Error });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, kubernetesId]);

  if (state.kind === 'loading') {
    return (
      <>
        <Typography variant="subtitle2">Kubernetes</Typography>
        <Progress />
      </>
    );
  }
  if (state.kind === 'error') {
    return (
      <>
        <Typography variant="subtitle2">Kubernetes</Typography>
        <ResponseErrorPanel error={state.error} />
      </>
    );
  }

  const { environments, clusters } = state.response;

  if (environments.length === 0) {
    const reachable = clusters.filter(c => c.status === 'ok');
    return (
      <>
        <Typography variant="subtitle2">Kubernetes</Typography>
        <Typography variant="body2">
          {reachable.length === 0
            ? 'Kubernetes cost is currently unavailable.'
            : `No cost data for this service yet. Allocations appear once pods carrying the ${KUBERNETES_ID_ANNOTATION} label have been running for a while.`}
        </Typography>
        <ClusterCaption clusters={clusters} />
      </>
    );
  }

  const rows = environments.map(toRow);
  if (environments.length > 1) {
    const total = environments.reduce(
      (sum, cost) => ({
        environment: 'Total',
        allocation: addAllocations(sum.allocation, cost.allocation),
      }),
      {
        environment: 'Total',
        allocation: {
          cpuCost: 0,
          ramCost: 0,
          pvCost: 0,
          networkCost: 0,
          loadBalancerCost: 0,
          totalCost: 0,
        },
      },
    );
    rows.push(toRow(total));
  }

  return (
    <>
      <Typography variant="subtitle2">Kubernetes</Typography>
      <Table
        options={{
          search: false,
          paging: false,
          toolbar: false,
          padding: 'dense',
        }}
        columns={columns}
        data={rows}
      />
      <ClusterCaption clusters={clusters} />
    </>
  );
};
